import React, { useCallback, useEffect, useRef, useState } from 'react';
import './game.css';
import rollSound from '../../assets/sounds/roll.wav';
import stompSound from '../../assets/sounds/stomp.wav';
import useGame from '../../hooks/useGame';
import {
    VALID_MOVE, PLAYER_NAMES, PLAYER_COLORS, PLAYER_PIECES,
} from '../../gameConstants';

export default function Game({ darkMode }) {
    const game = useGame();
    const [confirmRestart, setConfirmRestart] = useState(false);
    const timerRef = useRef(null);

    const {
        board, gamePhase, currentPlayer, rolledNumber, pieceSelected,
        hasValidMoves, log, setLog, winningCells, isPlacementPhase, isMovementPhase,
        isGameOver, placementProgress,
        placePiece, roll, selectPiece, deselectPiece, movePiece,
        skipTurn, restart, registerSound,
    } = game;

    useEffect(() => {
        registerSound('stomp', document.getElementById('stomp-sound'));
        registerSound('roll', document.getElementById('roll-sound'));
    }, [registerSound]);

    useEffect(() => {
        if (confirmRestart) {
            timerRef.current = setTimeout(() => setConfirmRestart(false), 3000);
            return () => clearTimeout(timerRef.current);
        }
    }, [confirmRestart]);

    const handleRestart = useCallback(() => {
        if (isPlacementPhase && game.placementProgress === 0) {
            restart();
            return;
        }
        if (!confirmRestart) {
            setConfirmRestart(true);
            return;
        }
        clearTimeout(timerRef.current);
        setConfirmRestart(false);
        restart();
    }, [confirmRestart, isPlacementPhase, game.placementProgress, restart]);

    const handleCellClick = useCallback((row, col) => {
        if (isGameOver) return;

        if (isPlacementPhase) {
            placePiece(row, col);
            return;
        }

        if (isMovementPhase) {
            if (pieceSelected) {
                const [sr, sc] = pieceSelected;
                if (sr === row && sc === col) {
                    deselectPiece();
                    return;
                }
                if (board[row][col] === VALID_MOVE) {
                    movePiece(row, col);
                    return;
                }
                setLog("FIELD NOT VALID");
            } else {
                selectPiece(row, col);
            }
        }
    }, [isGameOver, isPlacementPhase, isMovementPhase, pieceSelected, board, placePiece, selectPiece, deselectPiece, movePiece, setLog]);

    const isWinningCell = useCallback((row, col) => {
        return winningCells.some(([r, c]) => r === row && c === col);
    }, [winningCells]);

    const isPieceSelected = useCallback((row, col) => {
        return pieceSelected && pieceSelected[0] === row && pieceSelected[1] === col;
    }, [pieceSelected]);

    return (
        <div className="game-container">
            <audio id="stomp-sound">
                <source src={stompSound} type="audio/wav" />
            </audio>
            <audio id="roll-sound">
                <source src={rollSound} type="audio/wav" />
            </audio>

            <div className="game">
                <div className={`game-left ${darkMode ? "dark-mode" : "light-mode"}`}>
                    {!isGameOver && (
                        <button
                            className={`btn ${darkMode ? "dark-background" : "light-background"} ${confirmRestart ? "btn-confirm" : ""}`}
                            onClick={handleRestart}
                        >
                            {confirmRestart ? "Confirm?" : "Restart"}
                        </button>
                    )}

                    <div className="text-block">
                        <div className="info player-indicator">
                            <span>
                                {!isGameOver && gamePhase
                                    ? "Now playing: "
                                    : '\u00A0'}
                            </span>
                            {!isGameOver && gamePhase ? (
                                <span className="player-badge" style={{ backgroundColor: PLAYER_COLORS[currentPlayer] }}>
                                    {PLAYER_NAMES[currentPlayer].toUpperCase()}
                                </span>
                            ) : (
                                <span>&nbsp;</span>
                            )}
                        </div>

                        {isPlacementPhase && (
                            <div className="info phase-info">
                                Phase 1: Placement ({placementProgress}/15)
                            </div>
                        )}
                        {isMovementPhase && (
                            <div className="info phase-info">
                                Phase 2: Movement
                            </div>
                        )}

                        <div id="log" className={`info log ${log ? "log-active" : ""}`}>
                            {log || '\u00A0'}
                        </div>
                    </div>
                </div>

                {isGameOver && (
                    <div className="game-over">
                        <div className="game-over-content">
                            <div className="game-over-title">Winner!</div>
                            <div className="player-badge game-over-badge" style={{ backgroundColor: PLAYER_COLORS[(currentPlayer + 2) % 3] }}>
                                {PLAYER_NAMES[(currentPlayer + 2) % 3].toUpperCase()}
                            </div>
                            <button
                                className={`btn game-over-btn ${darkMode ? "dark-background" : "light-background"} ${confirmRestart ? "btn-confirm" : ""}`}
                                onClick={handleRestart}
                            >
                                {confirmRestart ? "Confirm?" : "Play again"}
                            </button>
                        </div>
                    </div>
                )}

                <table className={`game-table ${darkMode ? "dark-background" : "light-background"} ${isGameOver ? "hide" : ""}`}>
                    <tbody>
                        {board.map((row, rowIndex) => (
                            <tr key={`row${rowIndex}`}>
                                {row.map((cell, colIndex) => {
                                    const isWin = isWinningCell(rowIndex, colIndex);
                                    const isSelected = isPieceSelected(rowIndex, colIndex);
                                    const isValidMove = cell === VALID_MOVE;
                                    const hasPiece = cell >= 1 && cell <= 3;

                                    let cellClass = "";
                                    if (isWin) cellClass += " winning-cell ";
                                    if (isSelected) cellClass += " selected-piece ";
                                    if (isValidMove) cellClass += ` valid-move ${darkMode ? "valid-move-dark" : "valid-move-light"} `;

                                    return (
                                        <td
                                            key={`cell${rowIndex}-${colIndex}`}
                                            id={`cell-${rowIndex}-${colIndex}`}
                                            className={cellClass}
                                        >
                                            <div
                                                className={`cell-content ${hasPiece || isValidMove ? "clickable" : ""}`}
                                                onClick={() => handleCellClick(rowIndex, colIndex)}
                                            >
                                                {hasPiece ? (
                                                    <img
                                                        className={`piece ${isWin ? "piece-win" : ""} ${isSelected ? "piece-selected" : ""}`}
                                                        src={`../images/${PLAYER_PIECES[cell - 1]}`}
                                                        alt={`player ${cell}`}
                                                    />
                                                ) : isValidMove ? (
                                                    <div className="valid-move-dot" />
                                                ) : null}
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div className={`game-right ${darkMode ? "dark-mode" : "light-mode"}`}>
                    <div className="block">
                        {isMovementPhase && rolledNumber === 0 ? (
                            <button
                                className={`btn ${darkMode ? "dark-background" : "light-background"}`}
                                onClick={roll}
                            >
                                Roll
                            </button>
                        ) : (
                            <button className="btn btn-hidden" disabled>Roll</button>
                        )}

                        <div className="rolled-number-container">
                            {rolledNumber > 0 ? (
                                <div className="rolled-number" key={rolledNumber}>
                                    {rolledNumber}
                                </div>
                            ) : (
                                <div className="rolled-number-placeholder" />
                            )}

                            {isMovementPhase && !hasValidMoves && rolledNumber > 0 ? (
                                <button
                                    className={`btn ${darkMode ? "dark-background" : "light-background"}`}
                                    onClick={skipTurn}
                                >
                                    Skip
                                </button>
                            ) : (
                                <button className="btn btn-hidden" disabled>Skip</button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
